import type {
	TopologyBoundaryCurrentMember,
	TopologyBoundaryMemberAlignmentCandidate,
	TopologyBoundaryMemberCorrespondenceInput,
	TopologyBoundaryMemberCorrespondenceResult,
} from '../../../../types/reference/sketch-entity-reference/TopologyBoundaryMemberCorrespondence';
import type {
	SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import type {
	GeomFingerprint,
} from '../model/ExternalGeometrySourceDescriptor';
import {
	resolveSketchEntityReferenceBoundaryMemberState,
} from '../model/SketchEntityReferenceRecord';
import {TopologyEndpointAnchorEvidenceResolver} from './TopologyEndpointAnchorEvidenceResolver';

/**
 * Face/Loop 历史边界成员到当前有序 Wire 的一对一对应器。
 *
 * 该模块只解释成员身份和序列变化，不读取仓储，也不创建、删除或更新草图对象。
 * 调用方因此可以先得到完整计划，再沿用既有事务和补偿路径原子执行。
 */
export class TopologyBoundaryMemberCorrespondenceResolver {
	/** 成员匹配与目标端点归属共用的 topology 端点证据。 */
	private readonly endpointAnchorEvidenceResolver =
		new TopologyEndpointAnchorEvidenceResolver();

	/**
	 * 在所有循环起点与两个遍历方向中选择证据最强的一对一成员对应。
	 *
	 * @param input 历史成员与当前 Wire 成员。
	 * @returns 匹配、当前新增和历史移除三类互斥结果。
	 */
	public resolve(
		input: TopologyBoundaryMemberCorrespondenceInput,
	): TopologyBoundaryMemberCorrespondenceResult {
		const previous = this.orderPreviousMembers(input.previous);
		if (previous.length === 0) {
			return {
				matches: [],
				inserted: [...input.current].sort(
					(left, right) => left.currentIndex - right.currentIndex,
				),
				removed: [],
			};
		}
		if (input.current.length === 0) {
			return {
				matches: [],
				inserted: [],
				removed: previous,
			};
		}
		const previousLineGeometry = this.createNormalizedLineGeometry(
			previous,
			reference => reference.referenceId,
			reference => this.resolvePreviousGeometryFingerprint(reference),
		);
		const currentLineGeometry = this.createNormalizedLineGeometry(
			input.current,
			current => current.currentIndex,
			current => current.source.locator.geomFingerprint,
		);

		/*
		 * 闭合 Wire 没有永久首边；同一物理环可能从任意成员开始枚举，也可能整体反向。
		 * 对所有合法表示执行相同线性对齐，可以把起点和方向差异留在模块内部。
		 */
		const candidates = this.createCycleVariants(input.current)
			.map(current => this.alignLinear(
				previous,
				current,
				previousLineGeometry,
				currentLineGeometry,
			));
		const best = candidates.reduce<TopologyBoundaryMemberAlignmentCandidate | null>(
			(selected, candidate) => (
				!selected || this.compareCandidates(candidate, selected) > 0
					? candidate
					: selected
			),
			null,
		);
		if (!best) {
			return {
				matches: [],
				inserted: [...input.current],
				removed: previous,
			};
		}

		// 输出重新按调用方原始 Wire 顺序收口，避免循环表示泄漏到创建批次。
		return {
			matches: [...best.result.matches].sort((left, right) => (
				this.resolvePreviousIndex(left.reference, previous)
				- this.resolvePreviousIndex(right.reference, previous)
			)),
			inserted: [...best.result.inserted].sort(
				(left, right) => left.currentIndex - right.currentIndex,
			),
			removed: [...best.result.removed].sort((left, right) => (
				this.resolvePreviousIndex(left, previous)
				- this.resolvePreviousIndex(right, previous)
			)),
		};
	}

	/**
	 * 按创建时的 Face/Wire 成员序号恢复历史循环顺序。
	 *
	 * @param references 历史边界成员。
	 * @returns 不依赖边物理 ID 的历史成员副本。
	 */
	private orderPreviousMembers(
		references: readonly SketchEntityReferenceRecord[],
	): SketchEntityReferenceRecord[] {
		return references
			.map((reference, inputIndex) => ({reference, inputIndex}))
			.sort((left, right) => (
				(left.reference.selectionIntent?.sourceIndex ?? left.inputIndex)
				- (right.reference.selectionIntent?.sourceIndex ?? right.inputIndex)
			))
			.map(item => item.reference);
	}

	/**
	 * 枚举一个闭合 Wire 的正反向循环表示。
	 *
	 * @param items 当前 Wire 原始遍历成员。
	 * @returns 每个方向和循环偏移对应的独立数组。
	 */
	private createCycleVariants(
		items: readonly TopologyBoundaryCurrentMember[],
	): TopologyBoundaryCurrentMember[][] {
		const forward = [...items];
		const directions = [forward, [...forward].reverse()];
		return directions.flatMap(direction => direction.map((_, offset) => (
			[
				...direction.slice(offset),
				...direction.slice(0, offset),
			]
		)));
	}

	/**
	 * 对一个已确定起点和方向的 Wire 表示执行保持顺序的一对一对齐。
	 *
	 * @param previous 已排序历史成员。
	 * @param current 当前循环的一个线性表示。
	 * @returns 当前表示的最佳对应候选。
	 */
	private alignLinear(
		previous: readonly SketchEntityReferenceRecord[],
		current: readonly TopologyBoundaryCurrentMember[],
		previousLineGeometry: ReadonlyMap<string, readonly number[]>,
		currentLineGeometry: ReadonlyMap<number, readonly number[]>,
	): TopologyBoundaryMemberAlignmentCandidate {
		const rows: Array<Array<TopologyBoundaryMemberAlignmentCandidate | null>> =
			Array.from({length: previous.length + 1}, () => (
				Array.from({length: current.length + 1}, () => null)
			));
		rows[0]![0] = this.createEmptyCandidate();

		for (let previousCount = 0; previousCount <= previous.length; previousCount += 1) {
			for (let currentCount = 0; currentCount <= current.length; currentCount += 1) {
				const candidate = rows[previousCount]![currentCount];
				if (!candidate) {
					continue;
				}

				const previousMember = previous[previousCount];
				const currentMember = current[currentCount];
				if (previousMember && currentMember) {
					const evidence = this.scorePair(
						previousMember,
						currentMember,
						previousLineGeometry,
						currentLineGeometry,
					);
					if (evidence !== null) {
						this.retainBestCell(
							rows,
							previousCount + 1,
							currentCount + 1,
							this.appendMatch(
								candidate,
								previousMember,
								currentMember,
								evidence,
								previousCount,
							),
						);
					}
				}
				if (previousMember) {
					this.retainBestCell(
						rows,
						previousCount + 1,
						currentCount,
						this.appendRemoved(candidate, previousMember),
					);
				}
				if (currentMember) {
					this.retainBestCell(
						rows,
						previousCount,
						currentCount + 1,
						this.appendInserted(candidate, currentMember),
					);
				}
			}
		}

		return rows[previous.length]![current.length] ?? this.createEmptyCandidate();
	}

	/**
	 * 计算一条旧成员与当前边能否对应及其物理证据强度。
	 *
	 * @param reference 历史成员。
	 * @param current 当前 Wire 成员。
	 * @returns 可匹配时返回正分；曲线类型冲突时返回 null。
	 */
	private scorePair(
		reference: SketchEntityReferenceRecord,
		current: TopologyBoundaryCurrentMember,
		previousLineGeometry: ReadonlyMap<string, readonly number[]>,
		currentLineGeometry: ReadonlyMap<number, readonly number[]>,
	): number | null {
		if (reference.sourceDescriptor.kind !== 'topology-edge') {
			return null;
		}
		const previousLocator = reference.sourceDescriptor.locator;
		const currentLocator = current.source.locator;
		if (
			previousLocator.curveKind
			&& currentLocator.curveKind
			&& previousLocator.curveKind !== currentLocator.curveKind
		) {
			return null;
		}

		let score = 1;
		if (
			previousLocator.persistentRef
			&& previousLocator.persistentRef === currentLocator.persistentRef
		) {
			score += 10_000;
		}
		if (
			previousLocator.semanticName
			&& previousLocator.semanticName === currentLocator.semanticName
		) {
			score += 1_000;
		}
		if (
			previousLocator.subEntityId
			&& previousLocator.subEntityId === currentLocator.subEntityId
		) {
			score += 100;
		}
		score += this.endpointAnchorEvidenceResolver.resolve(reference, current).score;
		score += this.scoreNormalizedLineGeometry(
			previousLineGeometry.get(reference.referenceId),
			currentLineGeometry.get(current.currentIndex),
		);
		return score;
	}

	/**
	 * 从历史记录读取创建或上一轮重建保存的几何指纹。
	 *
	 * 新记录优先使用来源 locator；旧记录缺少该字段时，直线仍可从 rebuildPayload 恢复。
	 */
	private resolvePreviousGeometryFingerprint(
		reference: SketchEntityReferenceRecord,
	): GeomFingerprint | undefined {
		if (
			reference.sourceDescriptor.kind === 'topology-edge'
			&& reference.sourceDescriptor.locator.geomFingerprint
		) {
			return reference.sourceDescriptor.locator.geomFingerprint;
		}
		return reference.rebuildPayload.projectionResultKind === 'line'
			? {
				kind: 'line',
				startWorld: [...reference.rebuildPayload.geometryPayload.startWorld],
				endWorld: [...reference.rebuildPayload.geometryPayload.endWorld],
			}
			: undefined;
	}

	/**
	 * 将一组直线指纹放入同一包围范围，消除平移和逐轴缩放对端点位置的影响。
	 *
	 * @param items 同一历史环或当前环中的成员。
	 * @param resolveKey 结果 Map 使用的成员稳定键。
	 * @param resolveFingerprint 成员的可选几何指纹。
	 * @returns 每条可用直线的 `[startX,startY,startZ,endX,endY,endZ]` 归一化签名。
	 */
	private createNormalizedLineGeometry<TKey extends string | number, TItem>(
		items: readonly TItem[],
		resolveKey: (item: TItem) => TKey,
		resolveFingerprint: (item: TItem) => GeomFingerprint | undefined,
	): ReadonlyMap<TKey, readonly number[]> {
		const lineItems = items.map(item => ({
			item,
			fingerprint: resolveFingerprint(item),
		})).filter((entry) => entry.fingerprint?.kind === 'line');
		if (lineItems.length === 0) {
			return new Map<TKey, readonly number[]>();
		}

		const points = lineItems.flatMap((entry) => {
			const fingerprint = entry.fingerprint;
			return fingerprint?.kind === 'line'
				? [fingerprint.startWorld, fingerprint.endWorld]
				: [];
		});
		const minimum = [0, 1, 2].map(axis => Math.min(...points.map(point => point[axis]!)));
		const maximum = [0, 1, 2].map(axis => Math.max(...points.map(point => point[axis]!)));
		const result = new Map<TKey, readonly number[]>();
		lineItems.forEach((entry) => {
			const fingerprint = entry.fingerprint;
			if (fingerprint?.kind !== 'line') {
				return;
			}
			const normalizePoint = (point: readonly number[]) => point.map((coordinate, axis) => {
				const range = maximum[axis]! - minimum[axis]!;
				return Math.abs(range) > 1e-12
					? (coordinate - minimum[axis]!) / range
					: 0.5;
			});
			result.set(resolveKey(entry.item), [
				...normalizePoint(fingerprint.startWorld),
				...normalizePoint(fingerprint.endWorld),
			]);
		});
		return result;
	}

	/**
	 * 比较两条已在各自整环内归一化的直线，允许边自身起终点方向翻转。
	 */
	private scoreNormalizedLineGeometry(
		previous: readonly number[] | undefined,
		current: readonly number[] | undefined,
	): number {
		if (!previous || !current || previous.length !== 6 || current.length !== 6) {
			return 0;
		}
		const directDistance = previous.reduce((sum, coordinate, index) => (
			sum + Math.abs(coordinate - current[index]!)
		), 0);
		const reversedCurrent = [
			...current.slice(3, 6),
			...current.slice(0, 3),
		];
		const reversedDistance = previous.reduce((sum, coordinate, index) => (
			sum + Math.abs(coordinate - reversedCurrent[index]!)
		), 0);
		const distance = Math.min(directDistance, reversedDistance);
		return Math.max(0, 600 - distance * 100);
	}

	/**
	 * 将匹配转移追加到动态规划候选。
	 */
	private appendMatch(
		candidate: TopologyBoundaryMemberAlignmentCandidate,
		reference: SketchEntityReferenceRecord,
		current: TopologyBoundaryCurrentMember,
		evidence: number,
		previousIndex: number,
	): TopologyBoundaryMemberAlignmentCandidate {
		return {
			result: {
				matches: [...candidate.result.matches, {reference, current, evidence}],
				inserted: candidate.result.inserted,
				removed: candidate.result.removed,
			},
			score: {
				evidence: candidate.score.evidence + evidence,
				matchCount: candidate.score.matchCount + 1,
				detachedMatchCount: candidate.score.detachedMatchCount + (
					resolveSketchEntityReferenceBoundaryMemberState(reference) === 'detached'
						? 1
						: 0
				),
				editCount: candidate.score.editCount,
				currentIndexPath: [...candidate.score.currentIndexPath, current.currentIndex],
				previousIndexPath: [...candidate.score.previousIndexPath, previousIndex],
			},
		};
	}

	/**
	 * 将历史移除转移追加到动态规划候选。
	 */
	private appendRemoved(
		candidate: TopologyBoundaryMemberAlignmentCandidate,
		reference: SketchEntityReferenceRecord,
	): TopologyBoundaryMemberAlignmentCandidate {
		return {
			result: {
				matches: candidate.result.matches,
				inserted: candidate.result.inserted,
				removed: [...candidate.result.removed, reference],
			},
			score: {
				...candidate.score,
				editCount: candidate.score.editCount + 1,
			},
		};
	}

	/**
	 * 将当前新增转移追加到动态规划候选。
	 */
	private appendInserted(
		candidate: TopologyBoundaryMemberAlignmentCandidate,
		current: TopologyBoundaryCurrentMember,
	): TopologyBoundaryMemberAlignmentCandidate {
		return {
			result: {
				matches: candidate.result.matches,
				inserted: [...candidate.result.inserted, current],
				removed: candidate.result.removed,
			},
			score: {
				...candidate.score,
				editCount: candidate.score.editCount + 1,
			},
		};
	}

	/**
	 * 只在新候选更优时覆盖动态规划单元格。
	 */
	private retainBestCell(
		rows: Array<Array<TopologyBoundaryMemberAlignmentCandidate | null>>,
		previousCount: number,
		currentCount: number,
		candidate: TopologyBoundaryMemberAlignmentCandidate,
	): void {
		const current = rows[previousCount]![currentCount];
		if (!current || this.compareCandidates(candidate, current) > 0) {
			rows[previousCount]![currentCount] = candidate;
		}
	}

	/**
	 * 比较两个完整或中间对齐候选。
	 *
	 * @returns left 更优返回正数，right 更优返回负数，相同返回零。
	 */
	private compareCandidates(
		left: TopologyBoundaryMemberAlignmentCandidate,
		right: TopologyBoundaryMemberAlignmentCandidate,
	): number {
		const scalarComparisons = [
			left.score.evidence - right.score.evidence,
			left.score.matchCount - right.score.matchCount,
			left.score.detachedMatchCount - right.score.detachedMatchCount,
			right.score.editCount - left.score.editCount,
		];
		const scalarDifference = scalarComparisons.find(value => value !== 0);
		if (typeof scalarDifference === 'number') {
			return scalarDifference;
		}

		/*
		 * 物理证据完全相同时选择原始 Wire 遍历索引字典序更小的路径。
		 * 这使一条旧 detached 边面对连续等价候选时只消费区间第一条。
		 */
		const pathLength = Math.max(
			left.score.currentIndexPath.length,
			right.score.currentIndexPath.length,
		);
		for (let index = 0; index < pathLength; index += 1) {
			const leftIndex = left.score.currentIndexPath[index] ?? Number.MAX_SAFE_INTEGER;
			const rightIndex = right.score.currentIndexPath[index] ?? Number.MAX_SAFE_INTEGER;
			if (leftIndex !== rightIndex) {
				return rightIndex - leftIndex;
			}
		}
		const previousPathLength = Math.max(
			left.score.previousIndexPath.length,
			right.score.previousIndexPath.length,
		);
		for (let index = 0; index < previousPathLength; index += 1) {
			const leftIndex = left.score.previousIndexPath[index] ?? Number.MAX_SAFE_INTEGER;
			const rightIndex = right.score.previousIndexPath[index] ?? Number.MAX_SAFE_INTEGER;
			if (leftIndex !== rightIndex) {
				return rightIndex - leftIndex;
			}
		}
		return 0;
	}

	/**
	 * 创建动态规划起点。
	 */
	private createEmptyCandidate(): TopologyBoundaryMemberAlignmentCandidate {
		return {
			result: {
				matches: [],
				inserted: [],
				removed: [],
			},
			score: {
				evidence: 0,
				matchCount: 0,
				detachedMatchCount: 0,
				editCount: 0,
				currentIndexPath: [],
				previousIndexPath: [],
			},
		};
	}

	/**
	 * 读取历史成员在已排序循环中的位置。
	 */
	private resolvePreviousIndex(
		reference: SketchEntityReferenceRecord,
		previous: readonly SketchEntityReferenceRecord[],
	): number {
		return previous.findIndex(candidate => candidate.referenceId === reference.referenceId);
	}
}
